<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-switch
          v-model="companySettings.amocrm_on"
          @change="saveCompanySettings"
          :label="$t('amocrm.amocrm_enabled')"
          outlined
        ></v-switch>
      </v-col>

      <template v-if="companySettings.amocrm_on">
        <v-col cols="12" v-if="isConnected">
          <v-alert type="success" dense>
            {{ $t('amocrm.connection_established') || 'Соединение с AmoCRM установлено' }}
          </v-alert>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="settings.client_id"
            :label="$t('amocrm.id_integration')"
            :rules="[v => !!v || 'Обязательное поле']"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="settings.client_secret"
            :label="$t('amocrm.secret_key')"
            :rules="[v => !!v || 'Обязательное поле']"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="settings.redirect_uri"
            :label="$t('amocrm.redirect_uri')"
            :placeholder="$t('amocrm.redirect_uri_placeholder') || 'https://yoursite.com/amocrm/callback'"
            :rules="[v => !!v || 'Обязательное поле']"
            outlined
            dense
            hint="Укажите URL вашего приложения, на который будет происходить переадресация после авторизации"
            persistent-hint
          ></v-text-field>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="settings.request_uri"
            :label="$t('amocrm.request_uri')"
            :placeholder="$t('amocrm.request_uri_placeholder') || 'https://yoursubdomain.amocrm.ru'"
            :rules="[v => !!v || 'Обязательное поле']"
            outlined
            dense
            hint="Например: https://yoursubdomain.amocrm.ru"
            persistent-hint
          ></v-text-field>
        </v-col>

        <v-col cols="12" v-if="!isConnected">
          <v-text-field
            v-model="settings.code"
            :label="$t('amocrm.code')"
            :placeholder="$t('amocrm.code_placeholder') || 'Код авторизации'"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="12" class="text-center">
          <v-btn color="primary" @click="saveSettings" class="mr-2">
            {{ $t("custom.save") || 'Сохранить' }}
            <v-icon class="ml-1">mdi-content-save-check-outline</v-icon>
          </v-btn>

          <v-btn v-if="!isConnected" color="success" @click="authorize" class="mr-2">
            {{ $t("amocrm.authorize") || 'Авторизоваться в AmoCRM' }}
            <v-icon class="ml-1">mdi-login</v-icon>
          </v-btn>

          <v-btn v-if="isConnected" color="error" @click="disconnect">
            {{ $t("amocrm.disconnect") || 'Отключить' }}
            <v-icon class="ml-1">mdi-link-off</v-icon>
          </v-btn>
        </v-col>
      </template>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          {{ $t('custom.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-tabs
      v-if="isConnected"
      v-model="activeTab"
      background-color="transparent"
      class="mt-4"
    >
      <v-tab>
        <v-icon left>mdi-sync</v-icon>
        Синхронизация
      </v-tab>
      <v-tab>
        <v-icon left>mdi-webhook</v-icon>
        Вебхуки
      </v-tab>
      <v-tab>
        <v-icon left>mdi-text-box-outline</v-icon>
        Логи
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="mt-2">
      <v-tab-item>
        <amo-crm-sync
          :company-settings="companySettings"
        />
      </v-tab-item>
      <v-tab-item>
        <amo-crm-webhooks
          :company-settings="companySettings"
        />
      </v-tab-item>
      <v-tab-item>
        <amo-crm-logs />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import AmoCrmSync from './AmoCrmSync.vue';
import AmoCrmWebhooks from './AmoCrmWebhooks.vue';
import AmoCrmLogs from './AmoCrmLogs.vue';

export default {
  name: 'AmoCrmSettings',
  components: {
    AmoCrmSync,
    AmoCrmWebhooks,
    AmoCrmLogs
  },
  props: {
    companyId: {
      type: Number,
      required: true
    },
    initialSettings: {
      type: Object,
      default: () => ({})
    },
    companySettings: {
      type: Object,
      default: () => ({
        amocrm_on: false
      })
    }
  },

  data() {
    return {
      settings: {
        client_id: '',
        client_secret: '',
        redirect_uri: window.location.origin + '/amocrm/callback',
        request_uri: '',
        code: ''
      },
      isConnected: false,
      activeTab: 0,
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    }
  },

  created() {
    // Инициализация данных из пропсов, если они есть
    if (this.initialSettings) {
      Object.keys(this.initialSettings).forEach(key => {
        if (this.settings.hasOwnProperty(key)) {
          this.settings[key] = this.initialSettings[key];
        }
      });
    }

    this.checkConnection();

    // Проверяем наличие кода авторизации в URL при возврате из amoCRM
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      this.settings.code = code;
      this.saveSettings();
    }
  },

  methods: {
    saveCompanySettings() {
      this.$emit('update:companySettings', this.companySettings);
      this.$emit('save-company');
    },

    checkConnection() {
      this.axios
        .get('/api/v1/integrations/amocrm', {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          if (response.data && response.data.access_token) {
            this.isConnected = true;
            this.settings.client_id = response.data.client_id;
            this.settings.client_secret = response.data.client_secret;
            this.settings.redirect_uri = response.data.redirect_uri;
            this.settings.request_uri = response.data.request_uri;
          }
        })
        .catch(error => {
          console.error('Ошибка при проверке соединения с AmoCRM', error);
          this.isConnected = false;
        });
    },

    saveSettings() {
      this.axios
        .put('/api/v1/integrations/amocrm', { amocrms: this.settings }, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar(
            this.$t('amocrm.settings_saved'),
            'success'
          );
          this.checkConnection();
          this.$emit('settings-saved', this.settings);
        })
        .catch(error => {
          this.showSnackbar(
            this.$t('amocrm.settings_error'),
            'error'
          );
          console.error(error);
        });
    },

    authorize() {
      if (!this.settings.client_id || !this.settings.redirect_uri || !this.settings.request_uri) {
        this.showSnackbar(
          this.$t('amocrm.fill_fields'),
          'error'
        );
        return;
      }

      const authUrl = `${this.settings.request_uri}/oauth/authorize?client_id=${this.settings.client_id}&state=state&mode=post_message&redirect_uri=${encodeURIComponent(this.settings.redirect_uri)}`;

      window.location.href = authUrl;
    },

    disconnect() {
      this.$swal({
        title: this.$t('amocrm.confirmation'),
        text: this.$t('amocrm.disconnect_confirm'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t('amocrm.disconnect'),
        cancelButtonText: this.$t('custom.cancel'),
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          this.axios
            .delete('/api/v1/integrations/amocrm', {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then(() => {
              this.showSnackbar(
                this.$t('amocrm.disconnected'),
                'success'
              );
              this.isConnected = false;
              this.settings = {
                client_id: '',
                client_secret: '',
                redirect_uri: window.location.origin + '/amocrm/callback',
                request_uri: '',
                code: ''
              };
              this.$emit('settings-changed', this.settings);
            })
            .catch(error => {
              this.showSnackbar(
                this.$t('amocrm.disconnect_error') || 'Ошибка при отключении интеграции',
                'error'
              );
              console.error(error);
            });
        }
      });
    },

    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
}
</script>
